import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Zap, Target, Users, FileText, Rocket, AlertTriangle } from "lucide-react";

// Static example library used as a fallback inspiration when the user has
// no live cross-surface signals yet (no incidents / overdue rounds / sprints).
// Keys mirror the four flight-plan lanes stored on the backend.
const FLIGHT_PLAN_EXAMPLES = {
  patient_safety: [
    "90 min of uninterrupted deep work on the hardest thing",
    "Write the first draft of the JC binder update",
    "Review fall risk protocols on Unit 400",
    "Close out the top incident in the queue",
    "Finish the policy I keep deferring",
  ],
  staff_culture: [
    "One-on-one with the person I keep postponing",
    "Recognize one team member in writing today",
    "Hard conversation I've been avoiding",
    "Check in with a direct report who looks drained",
    "Lunch or walk with someone outside my team",
  ],
  admin_regulatory: [
    "The one meeting I should decline or delegate",
    "Inbox zero for anything older than 48 hours",
    "One deadline I move forward a day",
    "Update policy documentation I keep putting off",
    "Complete required training module (30 min)",
  ],
  strategic_improvement: [
    "Advance the sprint item closest to done",
    "One 5 Whys on my top operational pattern",
    "Draft the memo I've been thinking about for a week",
    "Analyze last month's incident trends",
    "Read 20 pages of the leadership book on my desk",
  ],
};

/**
 * Today's Four Bets — the daily flight plan card.
 *
 * Pulls cross-surface suggestions (incidents / overdue rounds / open sprints)
 * from /api/dashboard/suggestions when entering edit mode and surfaces them
 * as clickable chips. Static FLIGHT_PLAN_EXAMPLES provide fallback inspiration.
 */
const FlightPlan = ({ plan, onSave }) => {
  const [editing, setEditing] = useState(!plan);
  const [formData, setFormData] = useState({
    patient_safety: plan?.patient_safety || "",
    staff_culture: plan?.staff_culture || "",
    admin_regulatory: plan?.admin_regulatory || "",
    strategic_improvement: plan?.strategic_improvement || "",
  });
  const [suggestions, setSuggestions] = useState(null);

  // Pull cross-surface suggestions only when entering edit mode — they're
  // the "pre-loaded ink" for today's bets.
  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    axios
      .get(`${API}/dashboard/suggestions`, { withCredentials: true })
      .then((res) => {
        if (!cancelled) setSuggestions(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [editing]);

  // Keys preserved for backend compatibility; labels + icons are director-grade.
  // Each area also maps to a suggestion bucket from /api/dashboard/suggestions.
  const areas = [
    { key: "patient_safety",        label: "Deep Work",  sub: "the hardest thing",   icon: Target,   color: "text-purple-300",  bgColor: "bg-purple-500/10 border-purple-500/25",  suggestionKey: "deep_work" },
    { key: "staff_culture",         label: "People",     sub: "one human move",      icon: Users,    color: "text-blue-300",    bgColor: "bg-blue-500/10 border-blue-500/25",    suggestionKey: "people" },
    { key: "admin_regulatory",      label: "Must-Ship",  sub: "commitments due",     icon: FileText, color: "text-amber-300",   bgColor: "bg-amber-500/10 border-amber-500/25",  suggestionKey: "must_ship" },
    { key: "strategic_improvement", label: "Growth",     sub: "leverage + learning", icon: Rocket,   color: "text-emerald-300", bgColor: "bg-emerald-500/10 border-emerald-500/25", suggestionKey: "growth" },
  ];

  const handleSave = () => {
    onSave(formData);
    setEditing(false);
  };

  const selectExample = (key, example) => {
    setFormData({ ...formData, [key]: example });
  };

  return (
    <Card className="bento-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Today's Four Bets
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Name one move in each lane. One sentence. Non-negotiable.
          </p>
        </div>
        {!editing && plan && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="edit-flight-plan-btn">
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {areas.map((area) => (
            <div key={area.key} className={`p-3 rounded-lg border ${area.bgColor}`}>
              <div className="flex items-center gap-2 mb-2">
                <area.icon className={`w-4 h-4 ${area.color}`} />
                <span className="text-sm font-semibold">{area.label}</span>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">{area.sub}</span>
              </div>
              {editing ? (
                <div className="space-y-2">
                  <Textarea
                    value={formData[area.key]}
                    onChange={(e) => setFormData({ ...formData, [area.key]: e.target.value })}
                    placeholder="One sentence. Specific."
                    className="text-sm bg-transparent min-h-[60px]"
                    data-testid={`flight-plan-${area.key}`}
                  />
                  {/* Cross-surface suggestions — pulled from live data */}
                  {suggestions?.[area.suggestionKey]?.length > 0 && (
                    <div className="space-y-1" data-testid={`flight-plan-suggestions-${area.key}`}>
                      <div className="text-[9px] uppercase tracking-wider text-purple-300/70 font-semibold flex items-center gap-1">
                        <Zap className="w-2.5 h-2.5" />
                        From your real day
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {suggestions[area.suggestionKey].slice(0, 3).map((sugg, idx) => (
                          <Badge
                            key={`sugg-${idx}`}
                            variant="outline"
                            className={`text-[11px] cursor-pointer hover:bg-white/10 truncate max-w-[240px] border-purple-500/40 ${
                              sugg.urgency === "high" ? "bg-red-500/10 text-red-300 border-red-500/40" : "text-purple-200"
                            }`}
                            onClick={() => selectExample(area.key, sugg.suggestion)}
                            title={sugg.suggestion}
                            data-testid={`flight-plan-suggestion-${sugg.source}`}
                          >
                            {sugg.urgency === "high" && <AlertTriangle className="w-2.5 h-2.5 mr-1" />}
                            {sugg.suggestion.length > 40 ? `${sugg.suggestion.slice(0, 40)}...` : sugg.suggestion}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Static example library (fallback inspiration) */}
                  <div className="flex flex-wrap gap-1">
                    {FLIGHT_PLAN_EXAMPLES[area.key].slice(0, 3).map((example, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-xs cursor-pointer hover:bg-white/10 truncate max-w-[180px]"
                        onClick={() => selectExample(area.key, example)}
                        title={example}
                      >
                        {example.slice(0, 30)}...
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {plan?.[area.key] || <span className="italic text-zinc-500">Not set</span>}
                </p>
              )}
            </div>
          ))}
        </div>
        {editing && (
          <div className="flex justify-end mt-4">
            <Button onClick={handleSave} data-testid="save-flight-plan-btn">
              Save Today's Bets
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FlightPlan;
